export type ScalePoint = readonly [number, number | null]

/** Drag down to compress the plotted changes; drag up to magnify them. */
export function draggedValueExtent(min: number, max: number, distance: number, height: number) {
  if (![min, max, distance, height].every(Number.isFinite) || max <= min || height <= 0) return null
  const center = min / 2 + max / 2
  const factor = Math.exp(Math.max(-12, Math.min(12, distance / Math.max(height, 40) * 4)))
  const halfRange = Math.max((max - min) / 2 * factor, Math.abs(center) * Number.EPSILON * 16 || Number.MIN_VALUE)
  const extent = { min: center - halfRange, max: center + halfRange }
  return Number.isFinite(extent.min) && Number.isFinite(extent.max) && extent.max > extent.min ? extent : null
}

/** Fit straight line series to the visible time window, including clipped segments. */
export function visibleValueExtent(
  series: readonly (readonly ScalePoint[])[],
  start: number,
  end: number,
): { min: number; max: number } | null {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null
  const left = Math.min(start, end)
  const right = Math.max(start, end)
  let min = Infinity
  let max = -Infinity
  const include = (value: number) => {
    min = Math.min(min, value)
    max = Math.max(max, value)
  }

  for (const points of series) {
    let previous: readonly [number, number] | null = null
    for (const [time, value] of points) {
      if (!Number.isFinite(time) || value == null || !Number.isFinite(value)) {
        previous = null
        continue
      }
      if (time >= left && time <= right) include(value)
      if (previous && time > previous[0] && time >= left && previous[0] <= right) {
        const [previousTime, previousValue] = previous
        const segmentLeft = Math.max(left, previousTime)
        const segmentRight = Math.min(right, time)
        const interpolate = (timestamp: number) => {
          const weight = (timestamp - previousTime) / (time - previousTime)
          return previousValue * (1 - weight) + value * weight
        }
        include(interpolate(segmentLeft))
        include(interpolate(segmentRight))
      }
      previous = [time, value]
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  const padding = max > min ? (max - min) * 0.08 : Math.abs(min) * 0.01 || 1
  return { min: min - padding, max: max + padding }
}
